import type {
  HighlightOption,
  OrderBy,
  SearchResultContentSource,
  SearchResultContentType,
  SearchResultItem,
  SortBy,
} from '../types';
import type { ReactNode } from 'react';
import type { ObjectTag } from '~/features/common/types/objectTag';
import type { SearchUser } from '~/features/common/hooks';
import type { Skill } from '~/elc/features/skills';
import { useEffect, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useGetInternalUsersQuery } from '~/features/common/hooks';
import { useDebouncedValue, useQueryParam } from '~/hooks';
import { useGetSkills } from '~/elc/features/skills';
import { MixPanel } from '~/common';
import { alphanumericSortStrings } from '~/utils';
import { useLearnerSearchResults, useGetObjectTags } from '../api';
import {
  ALGOLIA_SEARCH_DEBOUNCE_MS,
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
  ORDER_KEYS,
  OrderByOptions,
  SearchParamOptions,
  SKILL_LIMIT,
  SORT_KEYS,
  SortByOptions,
  TAG_LIMIT,
} from '../constants';
import { UpdateDate } from '../types';

export type SearchContextValue = {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  contentTypes: SearchResultContentType[];
  setContentTypes: (query: string[]) => void;
  contentSources: SearchResultContentSource[];
  setContentSources: (query: string[]) => void;
  selectedTags: string[];
  setSelectedTags: (tags: string[]) => void;
  setTagSearchTerm: (term: string) => void;
  tagSearchTerm: string;
  searchResults: SearchResultItem[];
  isLoading: boolean;
  isLoadingTags: boolean;
  isError: boolean;
  errorStatus?: number;
  filterUpdatedDate: UpdateDate;
  updateFilterUpdatedDate: (query: UpdateDate) => void;
  availableTags: ObjectTag[];
  sortBy: SortBy;
  setSortBy: (query: SortBy) => void;
  sortOrder: OrderBy;
  setSortOrder: (query: OrderBy) => void;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  resetFilters: () => void;
  areFiltersActive: boolean;
  selectedUpdatedByUserIds: string[];
  setSelectedUpdatedByUserIds: (query: string[]) => void;
  updatedBySearchTerm: string;
  setUpdatedBySearchTerm: (term: string) => void;
  updatedByFilterUsers: SearchUser[];
  updatedByFilterUsersHasError: boolean;
  availableSkills: Skill[];
  isLoadingSkills: boolean;
  selectedSkills: string[];
  setSelectedSkills: (skills: string[]) => void;
  setSkillSearchTerm: (term: string) => void;
  skillSearchTerm: string;
};

type SearchProviderProps = {
  children: ReactNode;
  highlightOption?: HighlightOption;
};

export const SearchContext = createContext<SearchContextValue | null>(null);

const sanitizeUrlSortParams = (sortBy, sortOrder, setSortBy, setSortOrder) => {
  if (!SORT_KEYS.includes(sortBy)) {
    setSortBy(SortByOptions.RELEVANCE);
  }
  if (!ORDER_KEYS.includes(sortOrder)) {
    setSortOrder(OrderByOptions.DESC);
  }
  // Special case for RELEVANCE sort option, since there is only one order option we need to default to it
  if (sortBy === SortByOptions.RELEVANCE) {
    setSortOrder(OrderByOptions.DESC);
  }
};

export const SearchProvider = ({ children, highlightOption }: SearchProviderProps) => {
  const [searchQuery, setSearchQuery] = useQueryParam<string>(SearchParamOptions.QUERY, null);
  const [contentTypes, setContentTypes] = useQueryParam<SearchResultContentType[]>(SearchParamOptions.TYPE, []);
  const [contentSources, setContentSources] = useQueryParam<SearchResultContentSource[]>(
    SearchParamOptions.CONTENT_SOURCE,
    [],
  );
  const [filterUpdatedDate, setFilterUpdatedDate] = useQueryParam<UpdateDate>(
    SearchParamOptions.UPDATED_DATE,
    UpdateDate.ALL_TIME,
  );
  const [updatedDateTouched, setUpdatedDateTouched] = useState(false);
  const updateFilterUpdatedDate = useCallback(
    (date: UpdateDate) => {
      setUpdatedDateTouched(true);
      setFilterUpdatedDate(date);
    },
    [setFilterUpdatedDate],
  );
  const [selectedTags, setSelectedTags] = useQueryParam<string[]>(SearchParamOptions.TAGS, []);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const tagsQuery = useGetObjectTags({ sortBy: 'popularity', limit: TAG_LIMIT, searchTerm: tagSearchTerm });

  const [selectedSkills, setSelectedSkills] = useQueryParam<string[]>(SearchParamOptions.SKILLS, []);
  const [skillSearchTerm, setSkillSearchTerm] = useState('');
  const getSkillsQuery = useGetSkills(skillSearchTerm, SKILL_LIMIT);

  const [selectedUpdatedByUserIds, setSelectedUpdatedByUserIds] = useQueryParam<string[]>(
    SearchParamOptions.UPDATED_BY,
    [],
  );
  const [updatedBySearchTerm, setUpdatedBySearchTerm] = useState('');
  const debouncedUpdatedBySearchTerm = useDebouncedValue(updatedBySearchTerm, ALGOLIA_SEARCH_DEBOUNCE_MS);
  const updatedByFilterQuery = useGetInternalUsersQuery({
    searchTerm: debouncedUpdatedBySearchTerm || '',
    excludedUserIds: selectedUpdatedByUserIds || [],
    enabled: Boolean(debouncedUpdatedBySearchTerm) || (selectedUpdatedByUserIds?.length ?? 0) > 0,
  });

  const [sortBy, setSortBy] = useQueryParam<SortBy>(SearchParamOptions.SORT_BY, DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useQueryParam<OrderBy>(SearchParamOptions.SORT_ORDER, DEFAULT_SORT_ORDER);

  useEffect(() => {
    if (sortBy !== DEFAULT_SORT_BY || sortOrder !== DEFAULT_SORT_ORDER) {
      sanitizeUrlSortParams(sortBy, sortOrder, setSortBy, setSortOrder);
    }
  }, [setSortBy, setSortOrder, sortBy, sortOrder]);

  const isAnyFilterSelected =
    (updatedDateTouched && filterUpdatedDate !== UpdateDate.ALL_TIME) ||
    (selectedTags && selectedTags.length > 0) ||
    (selectedSkills && selectedSkills.length > 0);

  useEffect(() => {
    // Trigger Mixpanel tracking if any filter is selected and not in edit mode
    if (isAnyFilterSelected) {
      MixPanel.track('learner_advanced_search_filters_applied', {
        skill_filters: alphanumericSortStrings(selectedSkills),
        tag_filters: alphanumericSortStrings(selectedTags),
        time_filter: filterUpdatedDate,
        type_filters: alphanumericSortStrings([...contentTypes, ...contentSources]),
        filter_by_tags: Boolean(selectedTags.length),
        filter_by_skills: Boolean(selectedSkills.length),
      });
    }
  }, [
    contentTypes,
    contentSources,
    filterUpdatedDate,
    isAnyFilterSelected,
    selectedSkills,
    selectedTags,
    selectedUpdatedByUserIds,
  ]);

  const searchApiResult = useLearnerSearchResults({
    query: searchQuery,
    types: contentTypes,
    tags: selectedTags,
    skills: selectedSkills, // TODO: add this later?
    updatedDate: filterUpdatedDate,
    updatedBy: selectedUpdatedByUserIds,
    contentSources,
    highlightOption,
  });

  const resetFilters = useCallback(() => {
    setContentTypes([]);
    setContentSources([]);
    setSelectedTags([]);
    setSelectedSkills([]);
    setFilterUpdatedDate(UpdateDate.ALL_TIME);
    setSelectedUpdatedByUserIds([]);
  }, [
    setContentTypes,
    setContentSources,
    setSelectedTags,
    setSelectedSkills,
    setFilterUpdatedDate,
    setSelectedUpdatedByUserIds,
  ]);

  const areFiltersActive = useMemo(
    () =>
      Boolean(
        contentTypes.length ||
          contentSources.length ||
          selectedTags.length ||
          selectedSkills.length ||
          updatedDateTouched ||
          selectedUpdatedByUserIds.length,
      ),
    [
      contentTypes.length,
      contentSources.length,
      selectedTags.length,
      selectedSkills.length,
      updatedDateTouched,
      selectedUpdatedByUserIds.length,
    ],
  );

  const value = useMemo(
    () => ({
      searchQuery,
      setSearchQuery,
      filterUpdatedDate,
      updateFilterUpdatedDate,
      contentTypes,
      setContentTypes,
      contentSources,
      setContentSources,
      selectedTags,
      setSelectedTags,
      setTagSearchTerm,
      tagSearchTerm,
      sortBy,
      setSortBy,
      sortOrder,
      setSortOrder,
      availableTags: tagsQuery.data || [],
      isLoadingTags: tagsQuery.isLoading,
      searchResults: searchApiResult.data?.pages?.map((page) => page.data).flat() || [],
      isLoading: searchApiResult.isLoading,
      isError: searchApiResult.isError,
      errorStatus: searchApiResult?.error?.status,
      hasNextPage: searchApiResult.hasNextPage,
      fetchNextPage: searchApiResult.fetchNextPage,
      isFetchingNextPage: searchApiResult.isFetchingNextPage,
      resetFilters,
      areFiltersActive,
      selectedUpdatedByUserIds,
      setSelectedUpdatedByUserIds,
      updatedBySearchTerm,
      setUpdatedBySearchTerm,
      updatedByFilterUsers: updatedByFilterQuery.data || [],
      updatedByFilterUsersHasError: updatedByFilterQuery.isError,
      availableSkills: getSkillsQuery.data?.skills || [],
      isLoadingSkills: getSkillsQuery.isLoading,
      selectedSkills,
      setSelectedSkills,
      setSkillSearchTerm,
      skillSearchTerm,
    }),
    [
      searchQuery,
      setSearchQuery,
      filterUpdatedDate,
      updateFilterUpdatedDate,
      searchApiResult,
      contentTypes,
      setContentTypes,
      contentSources,
      setContentSources,
      selectedTags,
      setSelectedTags,
      setTagSearchTerm,
      tagSearchTerm,
      tagsQuery,
      sortBy,
      setSortBy,
      sortOrder,
      setSortOrder,
      resetFilters,
      areFiltersActive,
      selectedUpdatedByUserIds,
      setSelectedUpdatedByUserIds,
      updatedBySearchTerm,
      setUpdatedBySearchTerm,
      updatedByFilterQuery,
      selectedSkills,
      setSelectedSkills,
      setSkillSearchTerm,
      skillSearchTerm,
      getSkillsQuery,
    ],
  );
  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
